import { createI18n } from "vue-i18n";

import en from "../locales/en.json";
import ru from "../locales/ru.json";

/**
 * Translations.
 *
 * LOCALES ARE PLAIN JSON, one file per language, loaded eagerly. There are two
 * of them and together they are a few kilobytes — code-splitting them would add
 * a loading state to the first paint of every screen to save less than one
 * icon's worth of bytes. When this grows past a handful of languages, switch
 * `MESSAGES` to `import.meta.glob` with dynamic imports; nothing else here
 * changes.
 *
 * ADDING A LANGUAGE is: drop `xx.json` beside the others, import it, and add it
 * to `LOCALES`. Key parity between files is checked by `npm run check:i18n`, so
 * a half-translated file is visible rather than silently falling back.
 */

const MESSAGES = { en, ru };

/** Shown in the switcher. `native` is deliberately in the language itself. */
export const LOCALES = [
  { code: "en", native: "English" },
  { code: "ru", native: "Русский" }
];

const STORAGE_KEY = "polygon_devportal_locale";

/**
 * The remembered choice, then the browser's preference, then English.
 *
 * `navigator.languages` is checked before `navigator.language` because a
 * developer with `["ru","en"]` set has expressed an order, and honouring only
 * the first entry throws away the fallback they configured.
 */
function initialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && MESSAGES[saved]) return saved;
  } catch (e) {
    // Storage blocked (private window, hardened browser). Not an error: fall
    // through to detection, which needs no storage.
  }

  const preferred = (
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || "en"]
  ).map((tag) => String(tag).toLowerCase().split("-")[0]);

  return preferred.find((code) => MESSAGES[code]) || "en";
}

export const i18n = createI18n({
  // The Composition API surface (`useI18n`), not the legacy global-only mode —
  // the whole portal is `<script setup>`.
  legacy: false,
  globalInjection: true,
  locale: initialLocale(),
  fallbackLocale: "en",
  messages: MESSAGES,
  // A key present in en but missing elsewhere falls back silently in
  // production. The parity check is the real guard, and console noise trains
  // developers to ignore the console.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV
});

export function setLocale(code) {
  if (!MESSAGES[code]) return;
  i18n.global.locale.value = code;
  document.documentElement.lang = code;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // The choice still applies for this session.
  }
}

/** Applied once at boot so the document reflects the detected language. */
export function applyInitialLocale() {
  document.documentElement.lang = i18n.global.locale.value;
}
